// Morning Ritual — neo-fintech re-skin. Two beats: a calm guided breath, then a
// real "mindset" decision (the founder habit vs the easy way). The growth choice
// earns a one-week demand EDGE in the engine, so discipline visibly pays off.

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { dayKeyFromDate } from '@biz-kid/engine';
import { useDispatch } from '@biz-kid/store';
import { Card, Eyebrow, Button } from '../../ui/kit.js';

export type MorningRitualProps = { onComplete: () => void };

const TOTAL_BREATHS = 3;
const INHALE_MS = 3600;
const EXHALE_MS = 3600;

type Dilemma = {
  situation: string;
  growth: { label: string; sub: string };
  easy: { label: string; sub: string };
};

const DILEMMAS: Dilemma[] = [
  {
    situation: 'It’s 6am. You’ve got orders to prep before school.',
    growth: { label: 'Get up & prep', sub: 'Action beats comfort' },
    easy: { label: 'Snooze an hour', sub: 'Feels good now…' },
  },
  {
    situation: 'You made a little profit yesterday.',
    growth: { label: 'Reinvest it', sub: 'Plant it, grow it' },
    easy: { label: 'Spend it on treats', sub: 'Gone by lunch' },
  },
  {
    situation: 'Friends want to game all afternoon. You have customers waiting.',
    growth: { label: 'Finish work first', sub: 'Then play, guilt-free' },
    easy: { label: 'Game now, work later', sub: 'If there’s time…' },
  },
  {
    situation: 'A customer left an unhappy message.',
    growth: { label: 'Fix it fast', sub: 'Speed builds trust' },
    easy: { label: 'Deal with it… someday', sub: 'Out of sight' },
  },
];

type Phase = { kind: 'breathe' } | { kind: 'mindset' } | { kind: 'result'; growth: boolean };

export function MorningRitual({ onComplete }: MorningRitualProps) {
  const dispatch = useDispatch();
  const [phase, setPhase] = useState<Phase>({ kind: 'breathe' });
  const [dilemma] = useState(() => DILEMMAS[Math.floor(Math.random() * DILEMMAS.length)]!);

  const choose = (growth: boolean) => {
    dispatch({
      kind: 'CompleteWakeUp',
      dayKey: dayKeyFromDate(new Date()),
      mindset: growth ? 'growth' : 'easy',
    });
    setPhase({ kind: 'result', growth });
  };

  return (
    <div
      className="bk-app"
      style={{ position: 'fixed', inset: 0, zIndex: 7, display: 'grid', placeItems: 'center', padding: 18 }}
    >
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <Eyebrow color="var(--brand)">GOOD MORNING</Eyebrow>
          <h1 style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 900, color: 'var(--text)' }}>
            WAKE UP. LOCK IN.
          </h1>
        </div>

        <AnimatePresence mode="wait">
          {phase.kind === 'breathe' && (
            <motion.div key="breathe" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <BreathCoach onDone={() => setPhase({ kind: 'mindset' })} />
            </motion.div>
          )}

          {phase.kind === 'mindset' && (
            <motion.div key="mindset" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <Card>
                <Eyebrow>TODAY'S MOVE</Eyebrow>
                <p style={{ margin: '8px 0 14px', fontSize: 16, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>
                  {dilemma.situation}
                </p>
                <div style={{ display: 'grid', gap: 10 }}>
                  <MindChoice tone="growth" label={dilemma.growth.label} sub={dilemma.growth.sub} onClick={() => choose(true)} />
                  <MindChoice tone="easy" label={dilemma.easy.label} sub={dilemma.easy.sub} onClick={() => choose(false)} />
                </div>
              </Card>
            </motion.div>
          )}

          {phase.kind === 'result' && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
              <Card style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40 }}>{phase.growth ? '⚡' : '🌤️'}</div>
                <h2 style={{ margin: '6px 0 4px', fontSize: 20, fontWeight: 900, color: 'var(--text)' }}>
                  {phase.growth ? 'EDGE UNLOCKED 🔥' : 'CHILL DAY. OK.'}
                </h2>
                <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 700 }}>
                  {phase.growth
                    ? 'You showed up. +10% BOOST this week.'
                    : 'No edge today. Tomorrow’s yours.'}
                </p>
                <Button tone="primary" size="lg" full onClick={onComplete}>
                  LET’S GO →
                </Button>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function MindChoice({
  tone,
  label,
  sub,
  onClick,
}: {
  tone: 'growth' | 'easy';
  label: string;
  sub: string;
  onClick: () => void;
}) {
  const growth = tone === 'growth';
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        textAlign: 'left',
        padding: 14,
        borderRadius: 14,
        cursor: 'pointer',
        fontFamily: 'inherit',
        background: growth ? 'var(--up-soft)' : 'var(--surface-2)',
        border: `1px solid ${growth ? 'var(--up)' : 'var(--border)'}`,
      }}
    >
      <span style={{ fontSize: 22 }}>{growth ? '💪' : '😌'}</span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)' }}>{sub}</span>
      </span>
      <span style={{ fontSize: 11, fontWeight: 800, color: growth ? 'var(--up)' : 'var(--text-3)' }}>
        {growth ? '+EDGE' : ''}
      </span>
    </motion.button>
  );
}

/** Guided breathing — a calm brand-tinted orb that grows on the inhale. */
function BreathCoach({ onDone }: { onDone: () => void }) {
  const [breath, setBreath] = useState(1);
  const [inhaling, setInhaling] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const tick = (b: number, phaseInhale: boolean) => {
      setInhaling(phaseInhale);
      timer.current = setTimeout(
        () => {
          if (cancelled) return;
          if (phaseInhale) tick(b, false);
          else if (b < TOTAL_BREATHS) {
            setBreath(b + 1);
            tick(b + 1, true);
          } else onDone();
        },
        phaseInhale ? INHALE_MS : EXHALE_MS,
      );
    };
    tick(1, true);
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card style={{ display: 'grid', placeItems: 'center', gap: 16, padding: 28 }}>
      <Eyebrow>BREATH {breath} / {TOTAL_BREATHS}</Eyebrow>
      <div style={{ position: 'relative', width: 180, height: 180, display: 'grid', placeItems: 'center' }}>
        <motion.div
          animate={{ scale: inhaling ? 1.25 : 0.8, opacity: inhaling ? 0.6 : 0.3 }}
          transition={{ duration: (inhaling ? INHALE_MS : EXHALE_MS) / 1000, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'radial-gradient(circle, var(--brand) 0%, transparent 70%)',
          }}
        />
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            background: 'var(--brand-soft)',
            border: '2px solid var(--brand)',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 800,
            fontSize: 15,
            color: 'var(--brand)',
          }}
        >
          {inhaling ? 'In…' : 'Out…'}
        </div>
      </div>
      <button
        type="button"
        onClick={onDone}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-3)',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 700,
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        I’M READY →
      </button>
    </Card>
  );
}
